import { FetchState } from '@lib/mobx/fetch-state';
import type { BaseResponse } from '@app/types/BaseResponse.ts';
import type { FetchStateConfig } from '@lib/mobx/fetch-state/types/FetchStateConfig.ts';
import type { FetchStateDataSourceSpecification } from '@lib/mobx/fetch-state/types/FetchStateDataSourceSpecification.ts';
import type { FetchStateGlobalMiddleware } from '@lib/mobx/fetch-state/types/FetchStateGlobalMiddleware.ts';

import { IS_SHARED_FETCH_STATE_ENABLED } from './fetchStateFlag.ts';
import { SharedAPPFetchState, SharedFetchState } from './shared.ts';

const active = IS_SHARED_FETCH_STATE_ENABLED ? SharedFetchState : FetchState;

export class APPFetchState<TData, TError = BaseResponse['error']> extends FetchState<
  BaseResponse<TData, TError>,
  BaseResponse<TData, TError>['error'] & TError
> {
  constructor(config?: FetchStateConfig) {
    super(config);

    if (IS_SHARED_FETCH_STATE_ENABLED) {
      return new SharedAPPFetchState<TData, TError>(config) as unknown as APPFetchState<TData, TError>;
    }
  }

  static killAll = (args?: { keep?: string[] }) => active.killAll(args);
  static killScope = (scope: object) => active.killScope(scope);
  static combineLoadingState = FetchState.combineLoadingState;

  static middleware = (key: string, config: FetchStateGlobalMiddleware) => {
    const disposeLocal = FetchState.middleware(key, config);
    const disposeShared = SharedFetchState.middleware(key, config);

    return () => {
      disposeShared();
      return disposeLocal();
    };
  };
}

const dataSources: FetchStateDataSourceSpecification<BaseResponse>[] = [
  {
    id: 'app-fetch-state-basic',
    is: (response) => 'isSuccess' in response,
    data: (response) => response.data,
    error: (response) => response.errorData || response.error,
    isSuccess: (response) => response.isSuccess,
  },
  {
    id: 'app-fetch-state-500',
    is: (response) => 'Code' in response,
    data: () => null,
    error: () => null,
    isSuccess: () => false,
  },
  {
    id: 'app-fetch-state-validation',
    is: (response) => 'errors' in response,
    data: () => null,
    error: (response) => (response as any).errors,
    isSuccess: () => false,
  },
];

dataSources.forEach((source) => {
  FetchState.configureDataSource(source);
  SharedFetchState.configureDataSource(source);
});
